import React, { useEffect } from 'react';
import { Container, Title, Text, List, ThemeIcon, Card, createStyles, Anchor, Group, Stack, Badge } from '@mantine/core';
import { IconReceiptRefund, IconAlertTriangle, IconCamera, IconX, IconTruckDelivery, IconClock, IconInfoCircle } from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
  wrapper: {
    paddingTop: 80,
    paddingBottom: 80,
    minHeight: '100vh',
    backgroundColor: '#050505',
    backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
    color: theme.colors.gray[3],
    [theme.fn.smallerThan('sm')]: {
      paddingTop: 60,
      paddingBottom: 40,
    },
  },

  title: {
    fontSize: 34,
    fontWeight: 800,
    marginBottom: theme.spacing.xl,
    color: theme.white,
    textAlign: 'center',
    [theme.fn.smallerThan('sm')]: {
      fontSize: 28,
      marginBottom: theme.spacing.md,
    },
  },

  sectionTitle: {
    fontSize: 22,
    fontWeight: 700,
    marginTop: theme.spacing.xl * 1.5,
    marginBottom: theme.spacing.md,
    color: theme.colors.cyan[4],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    marginTop: '35px',
    [theme.fn.smallerThan('sm')]: {
      fontSize: 18,
      marginTop: theme.spacing.lg,
    },
  },

  content: {
    fontSize: 16,
    lineHeight: 1.6,
    color: theme.colors.gray[4],
    [theme.fn.smallerThan('sm')]: {
      fontSize: 14,
    },
  },

  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    padding: theme.spacing.xl,
    borderRadius: theme.radius.lg,
    [theme.fn.smallerThan('sm')]: {
      padding: theme.spacing.md,
    },
  },

  list: {
    marginTop: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
    color: theme.colors.gray[3],
  }
}));

export default function CancellationPolicy() {
  const { classes } = useStyles();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={classes.wrapper}>
      <Container size="md">
        <Card className={classes.card}>
          <Title className={classes.title}>Refund & Replacement Policy</Title>

          <Group position="center" mb="xl">
            <Badge size="lg" color="cyan">Effective Date: 06-02-2026</Badge>
            <Badge size="lg" variant="outline" color="gray">CDP3D</Badge>
          </Group>

          <Text className={classes.content}>
            This Refund & Replacement Policy outlines the circumstances under which refunds or replacements may be issued for orders placed on our website.
          </Text>

          {/* Custom & Made-to-Order */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="orange" variant="light" radius="xl"><IconAlertTriangle size="1rem" /></ThemeIcon>
            Custom & Made-to-Order Products
          </Title>
          <Text className={classes.content}>
            All products are custom-made based on customer-uploaded 3D files and specifications. Due to the personalized nature of 3D printing, refunds or returns are not applicable once production has begun, except in cases outlined in this policy.
          </Text>

          {/* Eligibility */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="green" variant="light" radius="xl"><IconReceiptRefund size="1rem" /></ThemeIcon>
            Eligibility for Refund or Replacement
          </Title>
          <Text className={classes.content}>You may be eligible for a refund or replacement under the following conditions:</Text>
          <List spacing="xs" size="sm" center className={classes.list}>
            <List.Item>The product is damaged during transit</List.Item>
            <List.Item>The product has a manufacturing defect</List.Item>
            <List.Item>The product received is significantly different from the approved order specifications</List.Item>
            <List.Item>The product received has noticeable color differences compared to the selected or communicated color</List.Item>
          </List>
          <Text className={classes.content} mt="xs" weight={700} color="white">
            Requests must be raised within 7 days of delivery.
          </Text>

          {/* Proof & Verification */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="blue" variant="light" radius="xl"><IconCamera size="1rem" /></ThemeIcon>
            Proof & Verification
          </Title>
          <Text className={classes.content}>
            To be eligible for a refund or replacement, customers must provide clear image or video proof recorded at the time of opening the package, showing:
          </Text>
          <List spacing="xs" size="sm" center className={classes.list}>
            <List.Item>The outer packaging</List.Item>
            <List.Item>Inner packaging</List.Item>
            <List.Item>The condition of the product upon opening</List.Item>
          </List>
          <Text className={classes.content} mt="xs" size="sm">Additional images or videos may be requested if required for verification.</Text>

          {/* Partial Refunds */}
          <Title className={classes.sectionTitle}>Partial Refunds</Title>
          <Text className={classes.content}>
            In cases where only a portion of an order is affected, partial refunds or replacements may be issued for the affected item(s) only, rather than the entire order.
          </Text>

          {/* Non-Eligible Cases */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="red" variant="light" radius="xl"><IconX size="1rem" /></ThemeIcon>
            Non-Eligible Cases
          </Title>
          <Text className={classes.content}>Refunds or replacements will not be provided in the following situations:</Text>
          <List spacing="xs" size="sm" center className={classes.list}>
            <List.Item>Errors in the uploaded 3D file, dimensions, tolerances, or design intent provided by the customer</List.Item>
            <List.Item>Minor surface marks or variations inherent to the 3D printing process</List.Item>
            <List.Item>Damage caused due to misuse, mishandling, or modifications after delivery</List.Item>
            <List.Item>Requests raised after the eligibility window</List.Item>
          </List>

          {/* External Factors */}
          <Title className={classes.sectionTitle}>External Factors</Title>
          <Text className={classes.content}>
            Refunds or replacements will not be applicable for issues caused by factors beyond our reasonable control, including but not limited to courier delays, natural events, strikes, regulatory restrictions, or other logistical disruptions.
          </Text>

          {/* Replacement Process */}
          <Title className={classes.sectionTitle}>Replacement Process</Title>
          <Text className={classes.content}>
            If a replacement is approved, the product will be reprinted and shipped. Replacement timelines depend on production capacity and delivery location.
          </Text>

          {/* Refund Process */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="cyan" variant="light" radius="xl"><IconReceiptRefund size="1rem" /></ThemeIcon>
            Refund Process
          </Title>
          <List spacing="xs" size="sm" center className={classes.list}>
            <List.Item>Where a refund is approved, it will be processed to the original method of payment.</List.Item>
            <List.Item>Refunds are typically completed within <strong>7–14 business days</strong> after approval.</List.Item>
            <List.Item>Shipping charges, if any, are non-refundable, unless the issue is due to an error on our part.</List.Item>
          </List>

          {/* Order Cancellation */}
          <Title className={classes.sectionTitle}>
            <ThemeIcon color="red" variant="light" radius="xl"><IconClock size="1rem" /></ThemeIcon>
            Order Cancellation
          </Title>
          <Text className={classes.content}>
            Orders may only be cancelled before production begins. Once the printing process has started, cancellations are not permitted.
          </Text>

          {/* Policy Review */}
          <Title className={classes.sectionTitle}>Policy Review</Title>
          <Text className={classes.content}>
            All refund and replacement requests are subject to review based on provided evidence and production records.
          </Text>

          <Title className={classes.sectionTitle}>Contact</Title>
          <Text className={classes.content}>
            For more information regarding refunds or replacements, please <Anchor href="/contact-us" color="cyan">contact CDP3D</Anchor>.
          </Text>

        </Card>
      </Container>
    </div>
  );
}
